// @flow

import { useCallback, useEffect, useReducer } from 'react';

type State<R, S> = {|
  responses: R,

  /**
   * If true, the responses have been modified from their initial values
   */
  modified: boolean,

  /**
   * The list of steps, including the current one and possibly previous/future steps. This list
   * is maintained to enable forward and backward navigation, as well as animation between steps.
   */
  steps: S[],

  /**
   * The index of the current step in the steps list.
   */
  currentStepIndex: number,
|};

/** Actions used internally by the useQuestionFlow hook */
type Action<R, S> =
  | {| type: 'RESET', initialStep: S |}
  | {| type: 'SET_RESPONSES', responses: $Shape<R>, modified: boolean |}
  | {| type: 'GO_FORWARD', responses: $Shape<R> |}
  | {| type: 'GO_BACK' |};

type QuestionFlowParams<R, S> = {|
  /**
   * The first step in the flow
   */
  initialStep: S,

  /**
   * Default answers for the flow
   */
  initialResponses: $ReadOnly<R>,

  /**
   * Callback to get the next step in the flow given the current step and the current answers
   */
  getNextStep: (step: S, $ReadOnly<R>) => ?S,

  /**
   * Optional steps that count towards the progress count
   */
  progressSteps?: $ReadOnlyArray<S>,
|};

export type QuestionFlowResult<R, S> = {|
  ...State<R, S>,

  nextStep: ?S,

  /**
   * Record user responses without going to the next question. This is used for questions that
   * don't advance to the next step until the user taps the next button.
   *
   * @param responses - any updates to the current response selections
   * @param modified - if true, these updates are a modification from the user and not loading
   * initial values, so the modified state will be true.
   */
  setResponses: (responses: $Shape<R>, modified?: boolean) => mixed,

  /**
   * Go to the next question, optionally with updated responses. On steps that auto-advance, this
   * is called with the user's response, which is used to determine the next step. For other
   * steps, this is called with no update after the user taps the next button.
   */
  goForward: (responses?: $Shape<R>) => mixed,

  /**
   * Go back to the step before the current one
   */
  goBack: () => mixed,

  /**
   * Go back to the initial step and reset the modified state. This is called after saving and
   * closing the flow.
   */
  reset: () => mixed,
|};

/**
 * Create a generic question and answer flow. See useOnboardingState.jsx for usage details.
 */
const useQuestionFlow = <S, R: {}>({
  initialStep,
  initialResponses,
  getNextStep,
}: QuestionFlowParams<$Exact<R>, S>): QuestionFlowResult<$Exact<R>, S> => {
  // Memoize reducer to prevent duplicate dispatches
  const reducer = useCallback(
    (previousState, action) => {
      switch (action.type) {
        case 'RESET': {
          return {
            ...previousState,
            modified: false,
            steps: [action.initialStep],
            currentStepIndex: 0,
          };
        }

        case 'SET_RESPONSES':
          return {
            ...previousState,
            modified:
              previousState.modified ||
              (action.modified && Object.keys(action.responses).length > 0),
            responses: { ...previousState.responses, ...action.responses },
          };

        case 'GO_FORWARD': {
          const responses = { ...previousState.responses, ...action.responses };

          const nextStep = getNextStep(
            previousState.steps[previousState.currentStepIndex],
            responses,
          );

          const modified = previousState.modified || Object.keys(action.responses).length > 0;

          if (nextStep == null) {
            return {
              ...previousState,
              modified,
              responses,
            };
          }

          const currentStepIndex = previousState.currentStepIndex + 1;
          const steps = [...previousState.steps.slice(0, currentStepIndex), nextStep];
          return {
            ...previousState,
            responses,
            steps,
            modified,
            currentStepIndex,
          };
        }

        case 'GO_BACK':
          return {
            ...previousState,
            currentStepIndex: Math.max(0, previousState.currentStepIndex - 1),
          };

        default:
          throw new Error(`Invalid action type: ${(action.type: empty)}`);
      }
    },
    [getNextStep],
  );

  const [state, dispatch] = useReducer<State<$Exact<R>, S>, Action<R, S>>(reducer, {
    responses: initialResponses,
    modified: false,
    steps: [initialStep],
    currentStepIndex: 0,
  });

  const reset = useCallback(() => {
    dispatch({ type: 'RESET', initialStep });
  }, [initialStep]);

  const nextStep = getNextStep(state.steps[state.currentStepIndex], state.responses);

  const setResponses = useCallback((responses: $Shape<R>, modified: boolean = true) => {
    dispatch({ type: 'SET_RESPONSES', responses, modified });
  }, []);

  const goForward = useCallback((responses: $Shape<R> = {}) => {
    dispatch({ type: 'GO_FORWARD', responses });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' });
  }, []);

  // If the first step in the flow doesn't match the initial step passed in, reset the flow. This
  // can happen if the first step depends on some dynamic information.
  const firstStep = state.steps[0];
  useEffect(() => {
    if (firstStep !== initialStep) {
      dispatch({ type: 'RESET', initialStep });
    }
  }, [firstStep, initialStep]);

  return {
    ...state,
    nextStep,
    setResponses,
    goForward,
    goBack,
    reset,
  };
};

export default useQuestionFlow;
